import { useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, useTheme } from "@mui/material";
import { ArrowBackIosNew as BackIcon } from "@mui/icons-material";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { markNativeIntroSeen, requestStoreReview } from "../../../lib/platform";
import { logAppsFlyerEvent, requestTrackingConsent } from "../../../lib/appsflyer";
import { wasAccountJustCreated } from "../../auth/authFlash";
import { hapticSelection } from "../../../lib/haptics";
import { useAnalytics } from "../../../hooks/useAnalytics";
import {
  OnboardingAnswers,
  getOnboardingState,
  markOnboardingCompleted,
  saveOnboardingProgress,
} from "./onboardingStorage";
import {
  AI_SHOWCASE_COPY,
  ATT_PRIMER_COPY,
  AnswerKey,
  HONESTY_COPY,
  MISSION_COPY,
  ONBOARDING_STEPS,
  OnboardingStep,
  QUESTION_COUNT,
  RATING_COPY,
  SHOWCASE_COPY,
  affirmationCopy,
  buildLoaderStages,
  painCopy,
  questionIndexOf,
  questionsCompletedBefore,
} from "./onboardingSteps";
import { ComparisonChart, CompoundingChart } from "./OnboardingCharts";
import { stepVariants } from "./onboardingMotion";
import { GREEN } from "./steps/stepStyles";
import { HookStep } from "./steps/HookStep";
import { AiIntroStep } from "./steps/AiIntroStep";
import { CommitmentStep } from "./steps/CommitmentStep";
import { QuestionStep } from "./steps/QuestionStep";
import { InterstitialStep } from "./steps/InterstitialStep";
import { MomentStep } from "./steps/MomentStep";
import { LoaderStep } from "./steps/LoaderStep";
import { RevealStep } from "./steps/RevealStep";
import { PrivacyLockChip } from "./assets/PrivacyLockChip";
import { RatingStarChip } from "./assets/RatingStarChip";
import { ShowcaseChart } from "./assets/ShowcaseChart";
import { AiChatDemo } from "./assets/AiChatDemo";
import { AuroraGlow } from "../../auth/AuroraGlow";
import { EmblemMark } from "./assets/EmblemMark";
import { AuthSheetMode, OnboardingAuthSheet } from "./OnboardingAuthSheet";

// Long enough for the selection state (check pop + border flash) to land
// before the step advances.
const AUTO_ADVANCE_DELAY_MS = 450;

const stepKey = (step: OnboardingStep): string =>
  step.kind === "question" ? step.answerKey : step.kind;

/**
 * The native onboarding quiz (docs/mobile-onboarding-plan.md): a pre-auth,
 * once-per-install flow that builds a tailored starting plan from the user's
 * answers, then opens the auth sheet in place — the hard paywall follows from
 * the membership gate after the post-auth reload. Progress persists locally
 * so a killed app resumes. This component is the thin orchestrator (state,
 * persistence, analytics, transitions); every screen lives in steps/.
 */
const OnboardingFlow = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const reduced = useReducedMotion();
  const { capture } = useAnalytics();

  const initialState = useMemo(getOnboardingState, []);
  const [stepIndex, setStepIndex] = useState(
    Math.min(initialState.step, ONBOARDING_STEPS.length - 1),
  );
  const [answers, setAnswers] = useState<OnboardingAnswers>(
    initialState.answers,
  );
  const [pendingAnswer, setPendingAnswer] = useState<string | null>(null);
  const [direction, setDirection] = useState(1);
  // Reopens the auth sheet (in sign-in mode, showing the check-your-inbox
  // banner) after the post-sign-up session refetch remounts the whole flow.
  const [authMode, setAuthMode] = useState<AuthSheetMode | null>(() =>
    wasAccountJustCreated() ? "sign-in" : null,
  );
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Remote kill lever for the rating ask (a funnel change baked into a
  // binary): the step shows UNLESS the PostHog flag is explicitly on, so a
  // missing flag, flags still loading, or an unreachable PostHog can never
  // hide it by accident.
  const ratingAskDisabled =
    useFeatureFlagEnabled("disable-onboarding-rating-ask") === true;

  const step = ONBOARDING_STEPS[stepIndex];

  useEffect(() => {
    capture("onboarding_step_viewed", {
      step_index: stepIndex,
      step_key: stepKey(ONBOARDING_STEPS[stepIndex]),
    });
  }, [stepIndex, capture]);

  useEffect(
    () => () => {
      if (advanceTimer.current) clearTimeout(advanceTimer.current);
    },
    [],
  );

  const goToStep = (
    index: number,
    nextAnswers: OnboardingAnswers = answers,
  ) => {
    const bounded = Math.max(0, Math.min(index, ONBOARDING_STEPS.length - 1));
    setDirection(bounded >= stepIndex ? 1 : -1);
    setPendingAnswer(null);
    setStepIndex(bounded);
    saveOnboardingProgress(bounded, nextAnswers);
  };

  const handleBack = () => {
    let target = stepIndex - 1;
    // Never land back inside the loader — skip to the step before it.
    if (ONBOARDING_STEPS[target]?.kind === "loader") target -= 1;
    goToStep(target);
  };

  const answerQuestion = (answerKey: AnswerKey, key: string) => {
    if (pendingAnswer !== null) return;
    hapticSelection();
    const nextAnswers = { ...answers, [answerKey]: key };
    setAnswers(nextAnswers);
    setPendingAnswer(key);
    capture("onboarding_answer", { step_key: answerKey, answer: key });
    advanceTimer.current = setTimeout(
      () => goToStep(stepIndex + 1, nextAnswers),
      AUTO_ADVANCE_DELAY_MS,
    );
  };

  const toggleMultiAnswer = (answerKey: AnswerKey, key: string) => {
    hapticSelection();
    const current = (answers[answerKey] as string[] | undefined) ?? [];
    const next = current.includes(key)
      ? current.filter((item) => item !== key)
      : [...current, key];
    setAnswers({ ...answers, [answerKey]: next });
  };

  const submitMultiAnswer = (answerKey: AnswerKey) => {
    capture("onboarding_answer", {
      step_key: answerKey,
      answer: answers[answerKey] ?? [],
    });
    goToStep(stepIndex + 1);
  };

  // The reveal's CTA — completion is stamped when the sheet opens, so the
  // paywall can personalize even if auth happens on a later cold launch.
  const openSignUpSheet = () => {
    markNativeIntroSeen();
    markOnboardingCompleted();
    capture("onboarding_completed", { destination: "/sign-up" });
    logAppsFlyerEvent("af_tutorial_completion", {
      af_success: true,
      af_tutorial_id: "native_onboarding",
    });
    setAuthMode("sign-up");
  };

  const openSignInSheet = () => {
    markNativeIntroSeen();
    capture("onboarding_skipped", { step_index: stepIndex });
    setAuthMode("sign-in");
  };

  const showHeader = step.kind !== "hook" && step.kind !== "loader";
  const showSegments =
    showHeader &&
    // Beats before the first question have nothing to fill the segments with,
    // so an all-empty progress bar would read as broken rather than as
    // "not started".
    step.kind !== "aiIntro" &&
    step.kind !== "commitment" &&
    step.kind !== "attPrimer" &&
    step.kind !== "reveal" &&
    step.kind !== "ratingAsk";
  const headerMuted = isDark ? "#9ca3af" : "#64748b";

  const completedQuestions = questionsCompletedBefore(stepIndex);
  const activeQuestion = questionIndexOf(stepIndex);

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.palette.background.default,
        pt: "calc(env(safe-area-inset-top, 0px) + 12px)",
        pb: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
        px: 3,
      }}
    >
      {/* The auth screens' aurora field runs through the whole flow. */}
      <AuroraGlow />
      {/* Progress header (hidden on the hook and the loader) */}
      {showHeader && (
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 3,
            minHeight: 40,
          }}
        >
          <IconButton
            onClick={handleBack}
            aria-label="Back"
            sx={{ color: headerMuted, ml: -1 }}
          >
            <BackIcon sx={{ fontSize: 18 }} />
          </IconButton>
          {/* The reveal carries the brand emblem, centered, instead of
              segments (white on dark; brand green on light). The rating ask
              deliberately does NOT — its MomentStep already opens with the
              wordmark, and two brand marks on one screen read as clutter. */}
          {step.kind === "reveal" && (
            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                display: "flex",
                justifyContent: "center",
                pointerEvents: "none",
              }}
            >
              <EmblemMark height={34} color={isDark ? "#ffffff" : "#22c55e"} />
            </Box>
          )}
          {showSegments && (
            <Box sx={{ flex: 1, display: "flex", gap: 0.75 }}>
              {Array.from({ length: QUESTION_COUNT }, (_, segment) => {
                const filled = segment < completedQuestions;
                const active = segment === activeQuestion;
                // Glass track over the aurora; filled segments solid green.
                let segmentColor = isDark
                  ? "rgba(255,255,255,0.12)"
                  : "rgba(0,0,0,0.1)";
                if (filled) segmentColor = GREEN;
                if (active) segmentColor = "rgba(34, 197, 94, 0.5)";
                return (
                  <Box
                    key={segment}
                    sx={{
                      flex: 1,
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: segmentColor,
                      backdropFilter: "blur(8px)",
                      transition: "background-color 0.35s ease",
                    }}
                  />
                );
              })}
            </Box>
          )}
          {!showSegments && <Box sx={{ flex: 1 }} />}
        </Box>
      )}

      {/* Step content — direction-aware enter/exit */}
      <AnimatePresence mode="wait" custom={direction} initial={false}>
        <motion.div
          key={stepIndex}
          custom={direction}
          variants={reduced ? undefined : stepVariants}
          initial="enter"
          animate="center"
          exit="exit"
          style={{ flex: 1, display: "flex", flexDirection: "column", position: "relative" }}
        >
          {step.kind === "hook" && (
            <HookStep onStart={() => goToStep(1)} onSignIn={openSignInSheet} />
          )}

          {step.kind === "aiIntro" && (
            <AiIntroStep onContinue={() => goToStep(stepIndex + 1)} />
          )}

          {step.kind === "commitment" && (
            <CommitmentStep onContinue={() => goToStep(stepIndex + 1)} />
          )}

          {step.kind === "attPrimer" && (
            <MomentStep
              eyebrow={ATT_PRIMER_COPY.eyebrow}
              title={ATT_PRIMER_COPY.title}
              body={ATT_PRIMER_COPY.body}
              visual={<PrivacyLockChip />}
              ctaLabel={ATT_PRIMER_COPY.cta}
              onContinue={() => {
                // iOS presents the system prompt over the WebView; the flow
                // advances either way (Apple forbids gating on the answer).
                void requestTrackingConsent();
                goToStep(stepIndex + 1);
              }}
              centered
              brandTop
            />
          )}

          {step.kind === "question" && (
            <QuestionStep
              step={step}
              stepIndex={stepIndex}
              answers={answers}
              pendingAnswer={pendingAnswer}
              onAnswer={answerQuestion}
              onToggleMulti={toggleMultiAnswer}
              onSubmitMulti={submitMultiAnswer}
            />
          )}

          {step.kind === "affirmation" && (
            <InterstitialStep
              eyebrow="Your goal"
              copy={affirmationCopy(answers)}
              chart={<CompoundingChart />}
              ctaLabel="Keep going"
              onContinue={() => goToStep(stepIndex + 1)}
            />
          )}

          {step.kind === "pain" && (
            <InterstitialStep
              eyebrow="The honest truth"
              copy={painCopy(answers)}
              chart={<ComparisonChart />}
              ctaLabel="Show me a better way"
              onContinue={() => goToStep(stepIndex + 1)}
            />
          )}

          {step.kind === "mission" && (
            <MomentStep
              eyebrow={MISSION_COPY.eyebrow}
              title={MISSION_COPY.title}
              body={MISSION_COPY.body}
              ctaLabel={MISSION_COPY.cta}
              onContinue={() => goToStep(stepIndex + 1)}
              centered
              brandTop
            />
          )}

          {step.kind === "honesty" && (
            <MomentStep
              eyebrow={HONESTY_COPY.eyebrow}
              title={HONESTY_COPY.title}
              body={HONESTY_COPY.body}
              visual={<PrivacyLockChip />}
              ctaLabel={HONESTY_COPY.cta}
              onContinue={() => goToStep(stepIndex + 1)}
              centered
              brandTop
            />
          )}

          {step.kind === "showcase" && (
            <MomentStep
              eyebrow={SHOWCASE_COPY.eyebrow}
              title={SHOWCASE_COPY.title}
              body={SHOWCASE_COPY.body}
              visual={<ShowcaseChart />}
              ctaLabel={SHOWCASE_COPY.cta}
              onContinue={() => goToStep(stepIndex + 1)}
            />
          )}

          {step.kind === "aiShowcase" && (
            <MomentStep
              eyebrow={AI_SHOWCASE_COPY.eyebrow}
              title={AI_SHOWCASE_COPY.title}
              body={AI_SHOWCASE_COPY.body}
              visual={<AiChatDemo />}
              ctaLabel={AI_SHOWCASE_COPY.cta}
              onContinue={() => goToStep(stepIndex + 1)}
            />
          )}

          {step.kind === "loader" && (
            <LoaderStep
              stages={buildLoaderStages(answers)}
              onDone={() => goToStep(stepIndex + 1)}
            />
          )}

          {step.kind === "reveal" && (
            <RevealStep
              answers={answers}
              onContinue={() => {
                if (ratingAskDisabled) {
                  openSignUpSheet();
                  return;
                }
                goToStep(stepIndex + 1);
              }}
            />
          )}

          {step.kind === "ratingAsk" && (
            <MomentStep
              eyebrow={RATING_COPY.eyebrow}
              title={RATING_COPY.title}
              body={RATING_COPY.body}
              visual={<RatingStarChip />}
              ctaLabel={RATING_COPY.cta}
              onContinue={() => {
                capture("rating_prompt_accepted");
                // iOS may or may not grant the sheet (3 per user per year,
                // no result reported) — the flow continues identically. The
                // short beat lets the system card land over this screen
                // before the auth drawer slides up beneath it.
                void requestStoreReview();
                advanceTimer.current = setTimeout(openSignUpSheet, 700);
              }}
              secondaryLabel={RATING_COPY.skip}
              onSecondary={() => {
                capture("rating_prompt_skipped");
                openSignUpSheet();
              }}
              centered
              brandTop
            />
          )}
        </motion.div>
      </AnimatePresence>

      <OnboardingAuthSheet
        open={authMode !== null}
        mode={authMode ?? "sign-up"}
        onClose={() => setAuthMode(null)}
        onModeChange={setAuthMode}
      />
    </Box>
  );
};

export default OnboardingFlow;
